// apps/web/src/features/requests/filters.ts
import {
  RequestListQuerySchema,
  RequestStatus,
  isStatusHiddenFromLogist,
  type RequestKind,
  type RequestListQuery,
} from '@magimp/shared';

import {
  detectStatusFilterRole,
  isLogistStatusFilterRole,
  type StatusFilterRole,
} from '../../shared/index.js';

// Re-exported so the requests barrel, the China-internal filter hooks/pages,
// and the router keep sourcing these role helpers from `./filters.js` unchanged
// (#1750: the definitions now live in `shared/statusFilterRole.ts`, shared by
// both list features and the router).
export { detectStatusFilterRole, isLogistStatusFilterRole, type StatusFilterRole };

/**
 * @aiPurpose canonical list of `/requests` filter keys that round-trip
 *   through the browser URL AND the `magimp:filters:requests` localStorage
 *   payload. `q`, `cursor`, `limit` are deliberately absent: search is
 *   local-only, pagination is per-session, and `limit` is a wire concern
 *   that the schema defaults to 50 on the receive side.
 * @aiInvariant adding a new persisted field requires updating BOTH this
 *   array AND the per-field parser/serializer/`urlToWireFilters` branches
 *   below. Loader and hook share these helpers, so any drift produces a
 *   TanStack query-key mismatch on shared URLs.
 */
export const PERSISTED_FILTER_KEYS = [
  'status',
  'slaState',
  'dateFrom',
  'dateTo',
  'weightFrom',
  'weightTo',
  'volumeFrom',
  'volumeTo',
  'managerId',
  'unread',
  'kind',
] as const;

export type PersistedFilterKey = (typeof PERSISTED_FILTER_KEYS)[number];

// WHY: stripped from canonical /requests URLs even when no persisted filter
//   key is present. `q` is local-only (search input never hydrates from URL);
//   `cursor` is per-session pagination; `limit` is a wire default.
const DISALLOWED_PERSIST_KEYS = ['q', 'cursor', 'limit'] as const;

function toSearchParams(url: URL | string | URLSearchParams): URLSearchParams {
  if (url instanceof URLSearchParams) return url;
  if (typeof url === 'string') return new URL(url, 'http://placeholder').searchParams;
  return url.searchParams;
}

// INVARIANT: the topbar "all" chip means "every status except ARCHIVE".
//   This is the single shared definition consumed by the page UI's
//   default `wireFilters.status` AND the route loader's prefetch key, so
//   that the warm-cache key matches the on-mount query key 1:1. If the
//   two diverge, every navigation issues an archive-inclusive request
//   the page never reads from, defeating the prefetch and re-introducing
//   the broad page-1 fetch the wire-side `q` predicate is meant to
//   avoid.
export const DEFAULT_REQUEST_LIST_STATUSES: readonly RequestStatus[] = [
  RequestStatus.DRAFT,
  // WHY (#960): READY_FOR_WORK is the pre-IN_WORK staging state. Once
  //   #961 starts emitting rows in this status, the default list view
  //   must include them or staging QA will think rows disappeared.
  //   Visual treatment (warning-tone pill) lands in #962; here we just
  //   keep the filter contract honest so the row remains visible.
  RequestStatus.READY_FOR_WORK,
  RequestStatus.IN_WORK,
  RequestStatus.RETURN,
  RequestStatus.PROCESSED,
  // WHY (#1836): POSTPONED is active/non-terminal and mirrors PROCESSED in
  //   the default "all except ARCHIVE" list, so a parked request stays
  //   visible without an explicit status chip. `getDefaultRequestListStatuses`
  //   keeps it visible to logists too (it is NOT in LOGIST_HIDDEN_STATUSES).
  RequestStatus.POSTPONED,
  RequestStatus.CLOSED,
  RequestStatus.SUCCESS,
  RequestStatus.REFUSED,
  // WHY (#2099): CANNOT_TRANSPORT is terminal — included in the default "all
  //   except ARCHIVE" set and kept visible to logists (NOT in
  //   LOGIST_HIDDEN_STATUSES), so getDefaultRequestListStatuses keeps it for
  //   logists too.
  RequestStatus.CANNOT_TRANSPORT,
] as const;

/**
 * @aiPurpose role-aware default-status set (#1159 PR5). Returns
 *   `DEFAULT_REQUEST_LIST_STATUSES` for non-logist actors; logists drop
 *   DRAFT / RETURN per `LOGIST_HIDDEN_STATUSES`. Consumed by the
 *   `/china-rates` and `/russia-rates` list pages' chip set + the
 *   `urlToWireFilters` fallback so the All chip never asks the backend
 *   for rows the actor cannot see anyway.
 */
export function getDefaultRequestListStatuses(role: StatusFilterRole): readonly RequestStatus[] {
  if (!isLogistStatusFilterRole(role)) return DEFAULT_REQUEST_LIST_STATUSES;
  return DEFAULT_REQUEST_LIST_STATUSES.filter((s) => !isStatusHiddenFromLogist(s));
}

/**
 * @aiPurpose narrow an arbitrary status list to the role-visible subset
 *   (#1159 PR5). Used to canonicalise hydrated URL / localStorage status
 *   selections so a shared link with `?status=DRAFT` collapses to "no
 *   chip selected" for a logist instead of issuing a wire query the
 *   backend will return empty for.
 */
export function filterRoleVisibleStatuses<S extends RequestStatus>(
  statuses: readonly S[],
  role: StatusFilterRole,
): S[] {
  if (!isLogistStatusFilterRole(role)) return [...statuses];
  return statuses.filter((s) => !isStatusHiddenFromLogist(s));
}

export function defaultRequestListFilters(): Partial<RequestListQuery> {
  return { status: [...DEFAULT_REQUEST_LIST_STATUSES] };
}

/**
 * @aiPurpose single source of truth for URL ↔ filter serialization on the
 *   `/requests` route. `parseFiltersFromUrl` reads `URL.searchParams`,
 *   coerces and validates against the shared `RequestListQuerySchema`, and
 *   discards any unknown / malformed key. `serializeFiltersToUrl` emits a
 *   canonical query string so the route loader, the list UI's URL writer,
 *   and `fetchRequests` all agree on the wire shape.
 * @aiInvariant the schema is the only place numeric / enum coercion lives;
 *   adding a new filter requires both a `RequestListQuerySchema` field and
 *   a serializer branch — never split that logic across consumers.
 */
export type RequestFilters = RequestListQuery;

export function parseFiltersFromUrl(url: URL | string): RequestFilters {
  const u = typeof url === 'string' ? new URL(url, 'http://placeholder') : url;
  const raw: Record<string, unknown> = {};
  // WHY: status, slaState and managerId are the repeated-key filters
  // (multi-select). Every other key takes its last value. Repeating any
  // non-multi key in the URL is a user mistake; we silently take the last
  // to match URLSearchParams.get.
  const statuses = u.searchParams.getAll('status');
  if (statuses.length > 0) raw['status'] = statuses;
  const slaStates = u.searchParams.getAll('slaState');
  if (slaStates.length > 0) raw['slaState'] = slaStates;
  const managerIds = u.searchParams.getAll('managerId');
  if (managerIds.length > 0) raw['managerId'] = managerIds;
  for (const [k, v] of u.searchParams.entries()) {
    // WHY: `status` / `slaState` / `managerId` are pre-aggregated above
    //   (multi-select). Drawer-state keys `openId` / `tab` coexist with
    //   filter keys on `/requests` and are owned by `useRequestUrlState`;
    //   the schema is `.strict()`, so leaking them through would make
    //   `safeParse` fail and silently reset filters to defaults whenever
    //   the drawer is open.
    if (k === 'status' || k === 'slaState' || k === 'managerId' || k === 'openId' || k === 'tab')
      continue;
    raw[k] = v;
  }
  const parsed = RequestListQuerySchema.safeParse(raw);
  if (parsed.success) return parsed.data;
  // WHY: a malformed URL falls back to defaults rather than blowing up the
  // route. The user sees an empty filter set and can re-edit; the server
  // never sees the malformed value.
  return RequestListQuerySchema.parse({});
}

/**
 * @aiPurpose returns true when `url` carries at least one persisted filter
 *   key (issue #630). Drives the hydration priority: URL > localStorage >
 *   defaults. A URL with only `q`/`cursor`/`limit` is NOT authoritative.
 */
export function hasPersistedFilterKey(url: URL | string | URLSearchParams): boolean {
  const sp = toSearchParams(url);
  for (const k of PERSISTED_FILTER_KEYS) {
    if (sp.has(k)) return true;
  }
  return false;
}

/**
 * UI-shape of persisted /requests filters (no `q`, `cursor`, `limit`).
 * Mirrors the persisted subset of `RequestListQuery`; `unread` is canonicalized
 * to `true | undefined` so `false` never round-trips.
 */
export interface PersistedRequestFilters {
  status?: RequestStatus[];
  slaState?: ('hot' | 'overdue')[];
  dateFrom?: string;
  dateTo?: string;
  weightFrom?: number;
  weightTo?: number;
  volumeFrom?: number;
  volumeTo?: number;
  managerId?: string[];
  unread?: true;
  // WHY (#1054): single-select kind chip. `STANDARD` or `CONTAINER`
  //   activates the matching chip; absence means "both kinds shown".
  //   No "any" value — the absence of the key IS the no-filter state.
  kind?: RequestKind;
}

// WHY: sort multi-value arrays in canonical output so the same logical
//   filter set produces the same URL/query-key regardless of click order
//   or URL key order. TanStack Query hashes array values positionally, so
//   an unsorted shape would split the cache for `?status=A&status=B` vs
//   `?status=B&status=A` and force a duplicate prefetch/list call.
function dedupSorted<T extends string>(arr: readonly T[]): T[] {
  return Array.from(new Set(arr)).sort();
}

function parseField<K extends keyof typeof RequestListQuerySchema.shape>(
  key: K,
  raw: unknown,
): unknown {
  const fieldSchema = RequestListQuerySchema.shape[key];
  const result = fieldSchema.safeParse(raw);
  return result.success ? result.data : undefined;
}

/**
 * @aiPurpose per-field partial parser for hydrating /requests filters from
 *   a URL (issue #630). Unlike `parseFiltersFromUrl` — which falls back to
 *   defaults on any whole-object failure — this helper validates each key
 *   in isolation and keeps valid siblings even when one field is malformed.
 *   Only persisted filter keys are read; `q`/`cursor`/`limit` are ignored
 *   by design so a shared bookmark never seeds the local search input.
 * @aiInvariant `unread` activates ONLY on literal `?unread=true`. Any other
 *   value (including `false`) is treated as absent. Date strings carry
 *   UTC-day semantics in v1 (`YYYY-MM-DDT00:00:00.000Z` start,
 *   `YYYY-MM-DDT23:59:59.999Z` end) — the calendar day is anchored in UTC,
 *   not the viewer's local zone. Empty multi-value strings (`?status=`) are
 *   dropped before the schema sees them so an invalid `status` does not
 *   discard the rest of the persisted payload.
 */
export function parsePersistedFiltersFromQuery(
  url: URL | string | URLSearchParams,
  role: StatusFilterRole = 'manager',
): PersistedRequestFilters {
  const sp = toSearchParams(url);
  const out: PersistedRequestFilters = {};
  const statuses = sp.getAll('status').filter((v) => v !== '');
  if (statuses.length > 0) {
    const data = parseField('status', statuses) as RequestStatus[] | undefined;
    if (data && data.length > 0) {
      // WHY (#1159 PR5): a shared link with `?status=DRAFT` from a
      //   logist actor silently collapses to "no chip selected" — the
      //   hydrated set drops to empty and the wire query falls back
      //   to the role-aware default. `useRequestsFilters` writes the
      //   canonical URL on the same effect, so the malformed param is
      //   replaced via `history.replaceState` without flashing the
      //   row.
      const narrowed = filterRoleVisibleStatuses(data, role);
      if (narrowed.length > 0) out.status = dedupSorted(narrowed);
    }
  }
  const slaStates = sp.getAll('slaState').filter((v) => v !== '');
  if (slaStates.length > 0) {
    const data = parseField('slaState', slaStates) as ('hot' | 'overdue')[] | undefined;
    if (data && data.length > 0) out.slaState = dedupSorted(data);
  }
  const managerIds = sp.getAll('managerId').filter((v) => v !== '');
  if (managerIds.length > 0) {
    const data = parseField('managerId', managerIds) as string[] | undefined;
    if (data && data.length > 0) out.managerId = dedupSorted(data);
  }
  for (const k of ['dateFrom', 'dateTo'] as const) {
    const v = sp.get(k);
    if (v != null && v !== '') {
      const data = parseField(k, v) as string | undefined;
      if (data) out[k] = data;
    }
  }
  for (const k of ['weightFrom', 'weightTo', 'volumeFrom', 'volumeTo'] as const) {
    const v = sp.get(k);
    if (v != null && v !== '') {
      const data = parseField(k, v) as number | undefined;
      if (data !== undefined) out[k] = data;
    }
  }
  // WHY: `BooleanQueryFlagSchema` accepts both `"true"` and `"false"`, but
  //   the chip has no inverse "read-only" semantics. Only literal `true`
  //   activates the chip; `false` and any other value are dropped so a
  //   shared bookmark cannot resurrect a non-existent inverse filter.
  if (sp.get('unread') === 'true') out.unread = true;
  // WHY (#1054): scalar single-select `kind`. Only the two literal
  //   wire values activate the chip; malformed values (empty, comma
  //   list, unknown enum) are dropped as absent per the persisted-filter
  //   defensive canonicalization pattern. The schema rejects them at
  //   the wire boundary too, so direct API callers still get
  //   `VALIDATION_ERROR` — this filter is the URL/storage-only path.
  const kindRaw = sp.get('kind');
  if (kindRaw === 'STANDARD' || kindRaw === 'CONTAINER' || kindRaw === 'SIMPLE') out.kind = kindRaw;
  return out;
}

/**
 * @aiPurpose canonical query-string payload for persisted filters
 *   (issue #630). Used as the localStorage value (without leading `?`) and
 *   as the URL canonicalization target. Keys appear in fixed
 *   `PERSISTED_FILTER_KEYS` order; multi-value arrays are deduped and
 *   sorted so two semantically equal filter sets serialize identically.
 *   `q`, `cursor`, `limit` are intentionally NOT emitted.
 */
export function serializePersistedFiltersToCanonicalQuery(
  filters: PersistedRequestFilters,
): string {
  const params = new URLSearchParams();
  if (filters.status && filters.status.length > 0) {
    for (const s of dedupSorted(filters.status)) params.append('status', s);
  }
  if (filters.slaState && filters.slaState.length > 0) {
    for (const s of dedupSorted(filters.slaState)) params.append('slaState', s);
  }
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.weightFrom !== undefined) params.set('weightFrom', String(filters.weightFrom));
  if (filters.weightTo !== undefined) params.set('weightTo', String(filters.weightTo));
  if (filters.volumeFrom !== undefined) params.set('volumeFrom', String(filters.volumeFrom));
  if (filters.volumeTo !== undefined) params.set('volumeTo', String(filters.volumeTo));
  if (filters.managerId && filters.managerId.length > 0) {
    for (const id of dedupSorted(filters.managerId)) params.append('managerId', id);
  }
  if (filters.unread === true) params.set('unread', 'true');
  // WHY (#1054): kind is a scalar single-select; emit only when active.
  if (filters.kind) params.set('kind', filters.kind);
  return params.toString();
}

/**
 * @aiPurpose derive the TanStack query-key shape (`Partial<RequestFilters>`)
 *   from a URL. Loader and hook MUST share this helper so a shared URL like
 *   `/requests?slaState=hot` produces a structurally equal query key in
 *   both places — otherwise the loader prefetches a slot the page never
 *   reads from, defeating the warm-cache invariant.
 * @aiInvariant `status` is ALWAYS populated: an empty/invalid URL persisted
 *   status falls back to `DEFAULT_REQUEST_LIST_STATUSES`, matching the All
 *   chip's wire shape. This is the only place the implicit default lives;
 *   never re-derive it in callers.
 */
export function urlToWireFilters(
  url: URL | string | URLSearchParams,
  role: StatusFilterRole = 'manager',
): Partial<RequestFilters> {
  const persisted = parsePersistedFiltersFromQuery(url, role);
  const wire: Partial<RequestFilters> = {};
  // INVARIANT (#630): sort the status array so the implicit-default fallback
  //   AND any user-selected subset both emit lexicographic order. Without
  //   this, click order in the UI would produce a different array shape
  //   than URL hydration, splitting the TanStack cache for the same
  //   logical filter set.
  // INVARIANT (#1159 PR5): the implicit-default fallback uses the
  //   role-aware status set so a logist's All chip and a manager's All
  //   chip emit structurally different wire keys — the wire key still
  //   round-trips with `useRequestsFilters` because that hook routes
  //   the same role through `getDefaultRequestListStatuses`.
  wire.status =
    persisted.status && persisted.status.length > 0
      ? dedupSorted(persisted.status)
      : dedupSorted([...getDefaultRequestListStatuses(role)]);
  if (persisted.slaState && persisted.slaState.length > 0) {
    wire.slaState = dedupSorted(persisted.slaState);
  }
  if (persisted.dateFrom) wire.dateFrom = persisted.dateFrom;
  if (persisted.dateTo) wire.dateTo = persisted.dateTo;
  if (persisted.weightFrom !== undefined) wire.weightFrom = persisted.weightFrom;
  if (persisted.weightTo !== undefined) wire.weightTo = persisted.weightTo;
  if (persisted.volumeFrom !== undefined) wire.volumeFrom = persisted.volumeFrom;
  if (persisted.volumeTo !== undefined) wire.volumeTo = persisted.volumeTo;
  if (persisted.managerId && persisted.managerId.length > 0) {
    wire.managerId = dedupSorted(persisted.managerId);
  }
  if (persisted.unread === true) wire.unread = true;
  if (persisted.kind) wire.kind = persisted.kind;
  return wire;
}

/**
 * @aiPurpose produce the next `URLSearchParams` for a filter-owned write:
 *   strip all persisted filter keys + the disallowed wire keys
 *   (`q`/`cursor`/`limit`), then set the canonical persisted keys.
 *   Drawer keys (`openId`/`tab`) and any unrelated tracking params survive
 *   untouched. The caller is responsible for preserving drawer-key
 *   ownership through `preserveRequestDrawerParams` in `useRequestUrlState`.
 */
export function applyCanonicalFilterParams(
  prev: URLSearchParams,
  filters: PersistedRequestFilters,
): URLSearchParams {
  const next = new URLSearchParams(prev);
  for (const k of PERSISTED_FILTER_KEYS) next.delete(k);
  for (const k of DISALLOWED_PERSIST_KEYS) next.delete(k);
  const canonical = new URLSearchParams(serializePersistedFiltersToCanonicalQuery(filters));
  for (const [k, v] of canonical.entries()) next.append(k, v);
  return next;
}
